import React, { useEffect, useMemo, useRef, useState } from "react";

import SeatMap, { PriceList, SeatLegend, seatKey, unitLabel } from "./SeatMap";

export const MAX_SEATS_PER_ORDER = 5;

// Render only while open: every opening starts from the confirmed seats.
// Seats are picked as a draft ([{ zone, number, ticketTypeId }]). "Təsdiqlə" and closing the popup
// (✕, the backdrop, Escape) both keep it, removed seats included; only "Ləğv et" throws it away.
// The seat's zone and section decide the ticket type; buyers choose only when several types apply.
// One order is paid for one ticket type, so every seat in it must be sold with the same one.
// seatPricing: buildSeatPricing(venue, ticketTypes), see components/SeatMap/pricing.js
const SeatPickerModal = ({ seatMap, hasError, onRetry, initialSeats, seatPricing, onConfirm, onClose }) => {
  const [draftSeats, setDraftSeats] = useState(initialSeats || []);
  const [notice, setNotice] = useState(null);
  const closeButtonRef = useRef(null);
  const keepDraftRef = useRef(null);

  const venue = seatMap?.venue;

  const seatDataOf = (zoneCode, number) => {
    const zone = venue?.zones?.find((item) => item.code === zoneCode);
    return { zone, seat: zone?.seats?.find((item) => item.number === number) };
  };

  const handleSelect = (zoneCode, number, seat) => {
    setNotice(null);
    const key = seatKey(zoneCode, number);
    const already = draftSeats.some((item) => seatKey(item.zone, item.number) === key);
    if (already) {
      setDraftSeats(draftSeats.filter((item) => seatKey(item.zone, item.number) !== key));
      return;
    }

    if (draftSeats.length >= MAX_SEATS_PER_ORDER) {
      setNotice(`Bir sifarişdə ən çox ${MAX_SEATS_PER_ORDER} yer seçmək olar.`);
      return;
    }

    const types = seatPricing.typesForSeat(zoneCode, seat);
    if (!types.length) return;

    // Everything in one order is paid for as one ticket type
    const orderTypeId = draftSeats[0]?.ticketTypeId;
    if (orderTypeId && !types.some(({ _id }) => _id === orderTypeId)) {
      setNotice("Bir sifarişdə yalnız eyni bilet növündən yerlər seçmək olar.");
      return;
    }

    setDraftSeats([...draftSeats, { zone: zoneCode, number, ticketTypeId: orderTypeId || types[0]._id }]);
  };

  // Escape to close, no page scroll behind, focus back to the opener afterwards
  useEffect(() => {
    const opener = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const handleKey = (e) => {
      if (e.key === "Escape") keepDraftRef.current();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKey);
    closeButtonRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKey);
      opener?.focus?.();
    };
  }, []);

  // A refreshed map may show a drafted seat as sold
  useEffect(() => {
    if (!seatMap || !draftSeats.length) return;
    const taken = new Set((seatMap.takenSeats || []).map(({ zone, number }) => seatKey(zone, number)));
    const left = draftSeats.filter((item) => !taken.has(seatKey(item.zone, item.number)));
    if (left.length !== draftSeats.length) {
      setDraftSeats(left);
      setNotice("Seçdiyiniz yerlərdən biri artıq satılıb.");
    }
  }, [seatMap, draftSeats]);

  // The types every chosen seat can be sold with; buyers pick when there are several
  const sharedTypes = useMemo(() => {
    if (!draftSeats.length) return [];
    return draftSeats.reduce((common, item, index) => {
      const { seat } = seatDataOf(item.zone, item.number);
      const types = seatPricing.typesForSeat(item.zone, seat);
      if (index === 0) return types;
      return common.filter(({ _id }) => types.some((type) => type._id === _id));
    }, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftSeats, seatPricing, venue]);

  const orderType = sharedTypes.find(({ _id }) => _id === draftSeats[0]?.ticketTypeId) || sharedTypes[0];
  const total = orderType ? orderType.price * draftSeats.length : 0;

  const chooseType = (typeId) => {
    setDraftSeats(draftSeats.map((item) => ({ ...item, ticketTypeId: typeId })));
  };

  const confirm = () => onConfirm(draftSeats.map((item) => ({ ...item, ticketTypeId: orderType?._id })));

  // Closing keeps what the buyer sees, so a seat removed here is gone from the form too.
  // An empty draft clears the choice; seats with no ticket type to sell them with cannot be kept.
  const keepDraft = () => (!draftSeats.length || orderType ? confirm() : onClose());
  keepDraftRef.current = keepDraft;

  return (
    <div className="relative z-50" role="dialog" aria-modal="true" aria-labelledby="seat-picker-title">
      <div className="fixed inset-0 bg-surface-950/70 backdrop-blur-md" onClick={keepDraft} />

      <div className="fixed inset-0 z-10 flex items-stretch sm:items-center justify-center sm:p-4 pointer-events-none">
        <div
          className="pointer-events-auto relative w-full h-full sm:h-[90vh] sm:max-w-5xl bg-white sm:rounded-3xl shadow-2xl border border-surface-100 overflow-hidden
            grid grid-cols-[minmax(0,1fr)] grid-rows-[auto_auto_minmax(0,1fr)_auto] lg:grid-cols-[minmax(0,1fr)_18rem] lg:grid-rows-[auto_minmax(0,1fr)_auto]"
        >
          {/* Header */}
          <div className="relative px-5 sm:px-8 py-4 sm:py-5 bg-gradient-to-br from-surface-950 to-surface-800 overflow-hidden lg:col-span-2">
            <div className="absolute inset-0 bg-gradient-mesh opacity-20" />
            <div className="relative flex items-center justify-between gap-4">
              <div>
                <h3 id="seat-picker-title" className="text-lg font-bold text-white">Yer seçin</h3>
                <p className="text-sm text-white/50">
                  {venue?.name || "Zal sxemi"} · ən çox {MAX_SEATS_PER_ORDER} yer
                </p>
              </div>
              <button
                ref={closeButtonRef}
                type="button"
                onClick={keepDraft}
                aria-label="Bağla"
                className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/70 hover:text-white transition-all"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Legend and prices: above the map on phones, in the side panel on desktop */}
          <div className="min-w-0 px-5 pt-4 lg:px-6 lg:pt-6 lg:col-start-2 lg:row-start-2 space-y-3 lg:space-y-5">
            <SeatLegend />
            {venue && (
              <div>
                <p className="hidden lg:block text-xs font-semibold uppercase tracking-wider text-surface-400 mb-2">Qiymətlər</p>
                <PriceList
                  items={seatPricing.priceList}
                  className="flex-nowrap overflow-x-auto pb-1 lg:flex-wrap lg:overflow-visible"
                />
              </div>
            )}
          </div>

          {/* Map */}
          <div className="p-4 sm:p-5 lg:pr-0 lg:col-start-1 lg:row-start-2 lg:row-span-2 min-h-0 min-w-0 flex flex-col">
            {venue ? (
              <SeatMap
                venue={venue}
                takenSeats={seatMap.takenSeats}
                selectedSeats={draftSeats}
                onSelect={handleSelect}
                seatPricing={seatPricing}
                className="flex-1"
              />
            ) : hasError ? (
              <div className="bg-red-50 text-red-700 text-sm px-4 py-3 rounded-2xl border border-red-100">
                Yer sxemini yükləmək olmadı.{" "}
                <button type="button" onClick={onRetry} className="font-semibold underline">
                  Yenidən cəhd edin
                </button>
              </div>
            ) : (
              <div className="flex-1 bg-surface-100 rounded-2xl animate-pulse" />
            )}
          </div>

          {/* Selection and actions */}
          <div className="border-t border-surface-100 lg:border-t-0 bg-surface-50 lg:bg-white px-5 py-4 lg:px-6 lg:py-6 lg:col-start-2 lg:row-start-3 flex flex-wrap items-center gap-3 lg:flex-col lg:flex-nowrap lg:items-stretch">
            <div className="flex-1 min-w-0">
              {draftSeats.length ? (
                <>
                  <div className="text-xs text-surface-500">
                    Seçilmiş {draftSeats.length > 1 ? `${draftSeats.length} yer` : "yer"}
                  </div>
                  <ul className="mt-0.5 space-y-0.5 max-h-28 overflow-y-auto">
                    {draftSeats.map((item) => {
                      const { zone } = seatDataOf(item.zone, item.number);
                      return (
                        <li key={seatKey(item.zone, item.number)} className="flex items-center gap-2 text-sm">
                          <span className="font-bold text-surface-900 truncate">
                            {zone?.name || item.zone} · {unitLabel(zone, item.number)}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleSelect(item.zone, item.number)}
                            aria-label={`${unitLabel(zone, item.number)} seçimini ləğv et`}
                            className="ml-auto shrink-0 text-surface-400 hover:text-red-600 transition"
                          >
                            ✕
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  {orderType && (
                    <div className="mt-1 text-sm font-semibold text-accent-700 truncate">
                      {orderType.name?.az} · {draftSeats.length} × {orderType.price}₼ = {total}₼
                    </div>
                  )}
                </>
              ) : (
                <div className="text-sm text-surface-500">Sxemdən boş yer seçin</div>
              )}
              {notice && <p className="mt-1.5 text-xs text-amber-700">{notice}</p>}
            </div>
            {sharedTypes.length > 1 && (
              <fieldset className="basis-full order-last lg:order-none">
                <legend className="text-xs text-surface-500 mb-1.5">Bilet növü</legend>
                <div className="flex flex-wrap gap-2 lg:flex-col">
                  {sharedTypes.map((type) => {
                    const isChecked = type._id === orderType?._id;
                    return (
                      <label
                        key={type._id}
                        className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm cursor-pointer transition ${
                          isChecked ? "border-accent-500 bg-accent-50 text-accent-700" : "border-surface-200 bg-white text-surface-700 hover:bg-surface-50"
                        }`}
                      >
                        <input
                          type="radio"
                          name="seat-ticket-type"
                          value={type._id}
                          checked={isChecked}
                          onChange={() => chooseType(type._id)}
                          className="form-radio h-4 w-4 text-accent-600 border-surface-300 focus:ring-accent-500"
                        />
                        <span className="font-medium">{type.name?.az}</span>
                        <span className="ml-auto pl-2 font-semibold">{type.price}₼</span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            )}
            <button
              type="button"
              onClick={onClose}
              className="hidden lg:block lg:order-last w-full rounded-2xl bg-surface-100 hover:bg-surface-200 text-surface-700 font-semibold py-3 text-sm transition"
            >
              Ləğv et
            </button>
            <button
              type="button"
              onClick={confirm}
              disabled={!draftSeats.length || !orderType}
              className="shrink-0 rounded-2xl bg-gradient-to-r from-accent-600 to-pink-600 text-white font-bold px-6 py-3 text-sm transition disabled:from-surface-200 disabled:to-surface-300 disabled:text-surface-500 disabled:cursor-not-allowed"
            >
              Təsdiqlə
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeatPickerModal;
